import { ExpandMore as ExpandIcon, Search } from "@mui/icons-material";
import { default as DeleteIcon } from "@mui/icons-material/HighlightOffRounded";
import {
  IconButton,
  InputAdornment,
  ListItemText,
  Select,
  SelectProps,
} from "@mui/material";
import React, { useState } from "react";
import { COLORS } from "../../styles/constants.style";
import { OptionsDataType } from "../select/MuiSelect";
import { MuiTextField } from "../textField/MuiTextfield";
import { MuiTypography } from "../typography/MuiTypography";
import {
  MuiSelectedListWrapper,
  searchWithInputStyle,
  SelectSearchInputWrapper,
} from "./MuiSelectedList.style";

type SelectedListProps = SelectProps & {
  label?: string;
  id?: string;
  selectedItems: OptionsDataType[];
  onRemove: (value: string | number) => void;
  className?: string;
  disabled?: boolean;
  requiredARIALabel: string;
};

export const MuiSelectedList = ({
  label,
  id,
  selectedItems,
  onRemove,
  className = "",
  disabled = false,
  requiredARIALabel = "Required",
}: SelectedListProps) => {
  const labelId = id ? `${id}-label` : undefined;

  const [filterText, setFilterText] = useState("");
  const [menuWidth, setMenuWidth] = useState<string | number>("auto");

  const handleSearchFilterChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setFilterText(event.target.value);
  };

  // Prevent default selection of an option when typing
  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    event.stopPropagation(); // Stops default selection behavior
  };

  // Filter options based on search input and if search is disabled, show all options
  const filteredOptions = selectedItems.filter((option) => {
    const labelMatch =
      typeof option.label === "string" &&
      option.label.toLowerCase().includes(filterText.toLowerCase());

    return labelMatch;
  });

  const handleOpen = (event: React.SyntheticEvent<Element, Event>) => {
    // Get the width of the Select component dynamically
    const selectBox = event.currentTarget.getBoundingClientRect();
    setMenuWidth(selectBox.width);
    setFilterText("");
  };

  const handleInputClick = (event: React.MouseEvent<HTMLInputElement>) => {
    event.stopPropagation();
  };

  return (
    <MuiSelectedListWrapper className={className}>
      <Select
        onOpen={handleOpen}
        displayEmpty
        IconComponent={ExpandIcon}
        className="mui-select"
        renderValue={() => (
          <MuiTypography
            className="select-label"
            variant="h4Medium"
            color={COLORS.cirrus_black5}
          >
            {label}
          </MuiTypography>
        )}
        MenuProps={{
          PaperProps: {
            sx: {
              width: menuWidth,
              ...searchWithInputStyle,
            },
          },
        }}
        inputProps={{
          "aria-labelledby": labelId,
          "data-testid": id,
          "aria-label": label,
          "aria-haspopup": "listbox",
          "aria-expanded": false,
          role: "combobox",
          "aria-controls": `${id}-listbox`,
        }}
        disabled={disabled}
      >
        <SelectSearchInputWrapper>
          <MuiTextField
            placeholder={"search"}
            className="mui-text-field select-search-input"
            value={filterText}
            onChange={handleSearchFilterChange}
            onKeyDown={handleSearchKeyDown}
            onClick={handleInputClick}
            showCloseIcon
            id="select-box-search"
            startAdornment={
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            }
            requiredARIALabel={requiredARIALabel}
          />
        </SelectSearchInputWrapper>

        {filteredOptions.map((option, index) => (
          // <MenuItem check accessibility not working here
          <div
            key={option.value}
            className="item-wrapper"
            // tabIndex={index}
            role="group"
            data-testid={"selected-item"}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation(); // don't close on click
            }}
          >
            <ListItemText primary={option.label} tabIndex={0} />
            <IconButton
              size="small"
              tabIndex={0}
              aria-label={option.label}
              data-testid="delete-button"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(option.value);
              }}
            >
              <DeleteIcon color="primary" />
            </IconButton>
          </div>
        ))}
      </Select>
    </MuiSelectedListWrapper>
  );
};
